// ScrollHintArrow.tsx
import React, { useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

type ScrollHintArrowProps = {
  pinId?: string;
  trigger?: React.RefObject<HTMLElement>;
  start?: string;
  end?: string;
  showFrom?: number;
  showUntil?: number;
  bounce?: boolean;
  bottom?: string;
  color?: string;
  className?: string;
  ariaLabel?: string;
};

const ArrowRoot = styled.button<{ $bottom: string }>`
  position: absolute;
  left: 50%;
  bottom: ${({ $bottom }) => $bottom};
  transform: translateX(-50%);
  opacity: 0;
  pointer-events: none;
  background: none;
  border: 0;
  padding: 0;
  line-height: 0;
  will-change: transform, opacity;

  svg {
    width: clamp(20px, 2vw, 35px);
    height: auto;
    display: block;
    filter: drop-shadow(0 2px 6px rgba(0,0,0,0.35));
    will-change: transform;
  }
`;

export default function ScrollHintArrow({
  pinId,
  trigger,
  start = 'top top',
  end = 'bottom top',
  showFrom = 0,
  showUntil,
  bounce = true,
  bottom = '3vh',
  color = '#ffffff',
  className,
  ariaLabel = 'Desplázate hacia abajo',
}: ScrollHintArrowProps) {
  const arrowRef = useRef<HTMLButtonElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // ⚠️ No usar overwrite:true aquí para no matar el bounce
      const show = () => gsap.to(arrowRef.current, { autoAlpha: 1, duration: 0.2, overwrite: 'auto' });
      const hide = () => gsap.to(arrowRef.current, { autoAlpha: 0, duration: 0.2, overwrite: 'auto' });

      // Bounce en el SVG (target distinto al del fade)
      const bounceTl = gsap.to(svgRef.current, {
        y: -8,
        duration: 0.8,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
        paused: !bounce,
      });

      const resolvePin = () => (pinId ? (ScrollTrigger.getById(pinId) as ScrollTrigger | null) : null);
      let pinST = resolvePin();

      const baseTrigger = pinST?.trigger || trigger?.current;
      const mainST = ScrollTrigger.create({
        id: pinId ? `${pinId}-arrow` : undefined,
        trigger: baseTrigger ?? trigger?.current,
        start: () => (pinST ? pinST.start : start),
        end: () => (pinST ? pinST.end : end),
        onToggle: (self) => {
          if (self.isActive) {
            const p = pinST ? pinST.progress : self.progress;
            const inWindow = p >= showFrom && (showUntil == null || p < showUntil);
            if (inWindow) { show(); bounceTl.play(); } else { hide(); bounceTl.pause(0); }
          } else {
            hide(); bounceTl.pause(0);
          }
        },
        onUpdate: (self) => {
          const p = pinST ? pinST.progress : self.progress;
          const inWindow = p >= showFrom && (showUntil == null || p < showUntil);
          if (self.isActive && inWindow) { show(); bounceTl.play(); } else { hide(); bounceTl.pause(0); }
        },
      });

      const refreshHandler = () => {
        const maybe = resolvePin();
        if (maybe && maybe !== pinST) {
          pinST = maybe;
          mainST.refresh();
        }
      };
      ScrollTrigger.addEventListener('refresh', refreshHandler);
      ScrollTrigger.refresh();

      return () => {
        ScrollTrigger.removeEventListener('refresh', refreshHandler);
        mainST.kill();
        bounceTl.kill();
      };
    });

    return () => ctx.revert();
  }, [pinId, trigger, start, end, showFrom, showUntil, bounce]);

  return (
    <ArrowRoot
      ref={arrowRef}
      className={className}
      aria-hidden
      $bottom={bottom}
      title="Scroll"
    >
      <svg
        ref={svgRef}
        width="38"
        height="43"
        viewBox="0 0 38 43"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={ariaLabel}
      >
        <path
          d="M2.35376 2.81152L17.5758 18.0335C18.3857 18.8434 19.6988 18.8434 20.5087 18.0335L35.7307 2.81152"
          stroke={color}
          strokeWidth="4"
          strokeLinecap="round"
        />
        <path
          d="M2.35376 24.5L17.5758 39.722C18.3857 40.5319 19.6988 40.5319 20.5087 39.722L35.7307 24.5"
          stroke={color}
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>
    </ArrowRoot>
  );
}
